import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import CustomAxios from '../utils/CustomAxios';

function getBodyTextFromComponents(components) {
  if (!Array.isArray(components)) return '';
  const body = components.find((c) => String(c?.type || '').toUpperCase() === 'BODY');
  return typeof body?.text === 'string' ? body.text : '';
}

function getHeaderComponent(components) {
  if (!Array.isArray(components)) return null;
  return components.find((c) => String(c?.type || '').toUpperCase() === 'HEADER') || null;
}

function getFooterComponent(components) {
  if (!Array.isArray(components)) return null;
  return components.find((c) => String(c?.type || '').toUpperCase() === 'FOOTER') || null;
}

function getButtonsComponent(components) {
  if (!Array.isArray(components)) return null;
  return components.find((c) => String(c?.type || '').toUpperCase() === 'BUTTONS') || null;
}

function statusChipColor(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED') return 'success';
  if (s === 'PENDING' || s === 'IN_APPEAL') return 'warning';
  if (s === 'REJECTED' || s === 'DISABLED') return 'error';
  return 'default';
}

function SectionCard({ title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        borderColor: '#E2E8F0',
        bgcolor: '#FAFAFA',
      }}
    >
      <Typography sx={{ fontFamily: 'inter', fontSize: '11px', fontWeight: 700, color: '#64748B', letterSpacing: '0.06em', mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function WhatsAppBubblePreview({ template }) {
  if (!template) return null;
  const components = template.components;
  const header = getHeaderComponent(components);
  const headerFmt = String(header?.format || '').toUpperCase();
  const previewHeaderImage =
    headerFmt === 'IMAGE' ? header?.example?.header_handle?.[0] || '' : '';
  let previewHeaderText = '';
  if (header) {
    if (typeof header.text === 'string' && header.text.trim()) previewHeaderText = header.text;
    else if (headerFmt && headerFmt !== 'IMAGE') previewHeaderText = `${headerFmt} header`;
  }
  const previewBodyText =
    getBodyTextFromComponents(components) || template.body || 'No template body available.';
  const footer = getFooterComponent(components);
  const buttons = getButtonsComponent(components);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5, background: '#E5DDD5', borderRadius: 2, minHeight: 120 }}>
      <Box
        sx={{
          height: 'fit-content',
          maxWidth: '92%',
          background: '#DCF8C6',
          borderRadius: '10px 10px 2px 10px',
          p: 1.25,
          boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
        }}
      >
        {previewHeaderImage ? (
          <Box
            component="img"
            src={previewHeaderImage}
            alt="Header"
            sx={{
              width: '100%',
              maxHeight: 180,
              objectFit: 'cover',
              borderRadius: 1,
              mb: 1,
              border: '1px solid #c5e7b5',
              background: '#f1f5f9',
            }}
          />
        ) : null}
        {previewHeaderText ? (
          <Typography
            sx={{
              fontFamily: 'inter',
              color: '#111B21',
              fontSize: '13px',
              fontWeight: 600,
              whiteSpace: 'pre-wrap',
              mb: 0.8,
            }}
          >
            {previewHeaderText}
          </Typography>
        ) : null}
        <Typography sx={{ fontFamily: 'inter', color: '#111B21', fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
          {previewBodyText}
        </Typography>
        {footer?.text ? (
          <Typography sx={{ fontFamily: 'inter', color: '#667781', fontSize: '12px', mt: 1 }}>
            {footer.text}
          </Typography>
        ) : null}
        {buttons?.buttons?.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
            {buttons.buttons.map((b, i) => (
              <Box
                key={i}
                sx={{
                  py: 0.75,
                  textAlign: 'center',
                  borderTop: '1px solid #c5e7b5',
                  fontFamily: 'inter',
                  fontSize: '13px',
                  color: '#027eb5',
                  fontWeight: 500,
                }}
              >
                {b?.text || b?.type || 'Button'}
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function copyToClipboard(text, onDone) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(() => onDone?.()).catch(() => {});
}

export function CommunicationWhatsAppTemplates() {
  const navigate = useNavigate();
  const [existingTemplates, setExistingTemplates] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [existingError, setExistingError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [copyHint, setCopyHint] = useState('');

  const fetchExistingTemplates = useCallback(async () => {
    try {
      setLoadingExisting(true);
      setExistingError('');
      const response = await CustomAxios.get('oasis/whatsapp/templates/');
      setExistingTemplates(response?.data?.data || []);
    } catch (error) {
      setExistingError(error?.response?.data?.error || error.message || 'Failed to fetch templates.');
    } finally {
      setLoadingExisting(false);
    }
  }, []);

  useEffect(() => {
    fetchExistingTemplates();
  }, [fetchExistingTemplates]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    existingTemplates.forEach((t) => {
      if (t.status) set.add(String(t.status));
    });
    return Array.from(set).sort();
  }, [existingTemplates]);

  const languageOptions = useMemo(() => {
    const set = new Set();
    existingTemplates.forEach((t) => {
      if (t.language) set.add(String(t.language));
    });
    return Array.from(set).sort();
  }, [existingTemplates]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    existingTemplates.forEach((t) => {
      if (t.category) set.add(String(t.category));
    });
    return Array.from(set).sort();
  }, [existingTemplates]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return existingTemplates.filter((t) => {
      if (filterStatus !== 'all' && String(t.status || '') !== filterStatus) return false;
      if (filterLanguage !== 'all' && String(t.language || '') !== filterLanguage) return false;
      if (filterCategory !== 'all' && String(t.category || '') !== filterCategory) return false;
      if (!q) return true;
      const name = String(t.name || '').toLowerCase();
      const body = getBodyTextFromComponents(t.components).toLowerCase();
      return name.includes(q) || body.includes(q);
    });
  }, [existingTemplates, search, filterStatus, filterLanguage, filterCategory]);

  const selected = useMemo(
    () => filteredTemplates.find((t) => String(t.id) === String(selectedId)) || null,
    [filteredTemplates, selectedId]
  );

  useEffect(() => {
    if (filteredTemplates.length === 0) {
      if (selectedId) setSelectedId('');
      return;
    }
    const stillThere = filteredTemplates.some((t) => String(t.id) === String(selectedId));
    if (!stillThere) {
      setSelectedId(String(filteredTemplates[0].id));
    }
  }, [filteredTemplates, selectedId]);

  const headerComp = selected ? getHeaderComponent(selected.components) : null;
  const footerComp = selected ? getFooterComponent(selected.components) : null;
  const buttonsComp = selected ? getButtonsComponent(selected.components) : null;
  const bodyText = selected ? getBodyTextFromComponents(selected.components) || selected.body || '' : '';

  return (
    <Box sx={{ width: '100%', marginTop: '30px', padding: '27px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/communication/whatsapp')}
          sx={{ color: '#288d2c', minWidth: 'auto' }}
        />
        <Typography sx={{ color: '#288d2c', fontFamily: 'inter', fontSize: '24px', fontWeight: 500 }}>
          WhatsApp templates
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          height: 'calc(100vh - 150px)',
          border: '1px solid #E2E8F0',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Left: list + filters */}
        <Box
          sx={{
            width: { xs: '100%', md: 360 },
            flexShrink: 0,
            borderRight: { md: '1px solid #E2E8F0' },
            borderBottom: { xs: '1px solid #E2E8F0', md: 'none' },
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#F8FAFC'
          }}
        >
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ fontFamily: 'inter', fontWeight: 600, fontSize: '15px', color: '#1E293B' }}>
                Templates
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchExistingTemplates}
                disabled={loadingExisting}
                sx={{
                  textTransform: 'none',
                  fontFamily: 'inter',
                  borderColor: '#CBD5E1',
                  color: '#1E293B',
                  fontSize: '12px',
                }}
              >
                Refresh
              </Button>
            </Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Search name or body…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5, bgcolor: '#FFF' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="tpl-filter-status">Status</InputLabel>
                <Select
                  labelId="tpl-filter-status"
                  label="Status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {statusOptions.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="tpl-filter-lang">Language</InputLabel>
                <Select
                  labelId="tpl-filter-lang"
                  label="Language"
                  value={filterLanguage}
                  onChange={(e) => setFilterLanguage(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {languageOptions.map((l) => (
                    <MenuItem key={l} value={l}>
                      {l}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="tpl-filter-cat">Category</InputLabel>
                <Select
                  labelId="tpl-filter-cat"
                  label="Category"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {categoryOptions.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            {loadingExisting && (
              <Typography sx={{ fontFamily: 'inter', color: '#94A3B8', fontSize: '13px', p: 2 }}>
                Loading…
              </Typography>
            )}
            {!loadingExisting && existingError && (
              <Typography sx={{ fontFamily: 'inter', color: '#DC2626', fontSize: '13px', p: 2, whiteSpace: 'pre-wrap' }}>
                {existingError}
              </Typography>
            )}
            {!loadingExisting && !existingError && filteredTemplates.length === 0 && (
              <Typography sx={{ fontFamily: 'inter', color: '#94A3B8', fontSize: '13px', p: 2 }}>
                No templates match your filters.
              </Typography>
            )}
            {!loadingExisting &&
              !existingError &&
              filteredTemplates.map((item) => {
                const active = String(item.id) === String(selectedId);
                return (
                  <Box
                    key={item.id}
                    onClick={() => setSelectedId(String(item.id))}
                    sx={{
                      p: 1.5,
                      mb: 0.75,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      border: active ? '2px solid #288d2c' : '1px solid #E2E8F0',
                      bgcolor: active ? '#F0FDF4' : '#FFF',
                      transition: 'background 0.15s, border-color 0.15s',
                      '&:hover': { bgcolor: active ? '#F0FDF4' : '#F1F5F9' },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'inter',
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#1E293B',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.name || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                      <Chip size="small" label={item.status || '-'} color={statusChipColor(item.status)} sx={{ height: 22, fontSize: '11px' }} />
                      {item.language ? (
                        <Chip size="small" variant="outlined" label={item.language} sx={{ height: 22, fontSize: '11px' }} />
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </Box>

        {/* Right: detail */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', bgcolor: '#FFF' }}>
          {!selected ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'inter', color: '#94A3B8', fontSize: '15px' }}>
                Select a template from the list
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2.5, borderBottom: '1px solid #E2E8F0' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'inter', fontWeight: 700, fontSize: '20px', color: '#0F172A', mb: 1 }}>
                      {selected.name || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                      <Chip size="small" label={selected.status || '-'} color={statusChipColor(selected.status)} />
                      {selected.language ? <Chip size="small" variant="outlined" label={`Lang: ${selected.language}`} /> : null}
                      {selected.category ? (
                        <Chip size="small" variant="outlined" label={selected.sub_category ? `${selected.category} · ${selected.sub_category}` : selected.category} />
                      ) : null}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Tooltip title="Copy template name">
                      <IconButton
                        size="small"
                        onClick={() => {
                          copyToClipboard(selected.name || '', () => {
                            setCopyHint('name');
                            setTimeout(() => setCopyHint(''), 1500);
                          });
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy template ID">
                      <IconButton
                        size="small"
                        onClick={() => {
                          copyToClipboard(String(selected.id || ''), () => {
                            setCopyHint('id');
                            setTimeout(() => setCopyHint(''), 1500);
                          });
                        }}
                      >
                        <Typography sx={{ fontSize: '11px', fontFamily: 'monospace' }}>ID</Typography>
                      </IconButton>
                    </Tooltip>
                    {copyHint ? (
                      <Typography sx={{ fontSize: '11px', color: '#166534', fontFamily: 'inter' }}>Copied</Typography>
                    ) : null}
                  </Box>
                </Box>
                {selected.id ? (
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '12px', color: '#64748B', mt: 1.5 }}>
                    Template ID: {selected.id}
                  </Typography>
                ) : null}
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography sx={{ fontFamily: 'inter', fontWeight: 600, fontSize: '13px', color: '#1E293B', mb: 1 }}>
                    Live preview
                  </Typography>
                  <WhatsAppBubblePreview template={selected} />
                </Box>

                <Divider />

                <Typography sx={{ fontFamily: 'inter', fontWeight: 600, fontSize: '13px', color: '#1E293B' }}>
                  Components (Meta)
                </Typography>

                <SectionCard title="HEADER">
                  {headerComp ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Typography sx={{ fontFamily: 'inter', fontSize: '13px', color: '#334155' }}>
                        <strong>Format:</strong> {headerComp.format || '—'}
                      </Typography>
                      
                    </Box>
                  ) : (
                    <Typography sx={{ fontFamily: 'inter', fontSize: '13px', color: '#94A3B8' }}>No header</Typography>
                  )}
                </SectionCard>

                <SectionCard title="BODY">
                  <Typography sx={{ fontFamily: 'inter', fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap' }}>
                    {bodyText || '—'}
                  </Typography>
                </SectionCard>

                <SectionCard title="FOOTER">
                  {footerComp?.text ? (
                    <Typography sx={{ fontFamily: 'inter', fontSize: '13px', color: '#334155' }}>
                      {footerComp.text}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontFamily: 'inter', fontSize: '13px', color: '#94A3B8' }}>No footer</Typography>
                  )}
                </SectionCard>

                <SectionCard title="BUTTONS">
                  {buttonsComp?.buttons?.length ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {buttonsComp.buttons.map((b, i) => (
                        <Chip
                          key={i}
                          size="small"
                          label={`${b?.text || b?.type || 'Button'}${b?.url ? ` · ${b.url}` : ''}`}
                          sx={{ fontFamily: 'inter' }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ fontFamily: 'inter', fontSize: '13px', color: '#94A3B8' }}>No buttons</Typography>
                  )}
                </SectionCard>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default CommunicationWhatsAppTemplates;
